import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useCampaigns } from "./hooks/use-campaigns";
import {
  getFormattedRunTime,
  getRegionFromLoadAddr,
  isCampaignInRegion,
  getParticipationLimitText,
  getRemainingParticipationCountText,
} from "./utils/campaign-utils";
import { SkeletonWave } from "../../components/common/skeleton-wave";
import Header from "../../components/common/header";
import ParticipationGuideSheet from "./components/participation-guide-sheet";
import MyBrandLikesShortcut from "./components/my-brand-likes-shortcut";
import MISSION_IMG from "./assets/mission.png";
import MONEY_IMG from "./assets/money.png";
import HOWTO_IMG from "./assets/howto.png";

const CampaignsListPage = () => {
  const navigate = useNavigate();
  const { campaigns, loading, isEligibleRegion } = useCampaigns(7);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const loadAddr = localStorage.getItem("loadAddr");
  const region = getRegionFromLoadAddr(loadAddr);

  // 지역 필터링 적용
  const filteredCampaigns = region
    ? campaigns.filter((campaign: any) => isCampaignInRegion(campaign, region))
    : campaigns;

  if (!isEligibleRegion) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            서비스 준비 중입니다
          </h1>
          <p className="text-gray-600">
            이 서비스는 성남/수원 지역에서만 이용 가능합니다.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <SkeletonWave className="pt-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header title="미션 할인 캠페인" showBackButton={true} />
      <div className="max-w-4xl mx-auto p-4">
        {/* 상단 숏컷 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* 내 미션 숏컷 */}
          <div
            onClick={() => navigate("/my-challenges")}
            className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  src={MISSION_IMG}
                  alt="내 미션"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-lg text-black">내 미션</h3>
                <p className="text-sm text-gray-600">진행 중인 미션 확인</p>
              </div>
            </div>
          </div>

          {/* 페이백 숏컷 */}
          <div
            onClick={() => navigate("/my/wallet")}
            className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  src={MONEY_IMG}
                  alt="페이백"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-lg text-black">페이백</h3>
                <p className="text-sm text-gray-600">페이백 내역 확인</p>
              </div>
            </div>
          </div>
        </div>

        {/* 참여방법 및 내 좋아요 브랜드 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* 참여방법 숏컷 */}
          <div
            onClick={() => setIsBottomSheetOpen(true)}
            className="bg-white rounded-lg shadow-md p-4 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  src={HOWTO_IMG}
                  alt="참여방법"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-lg text-black">참여방법</h3>
                <p className="text-sm text-gray-600">미션 참여 방법 안내</p>
              </div>
            </div>
          </div>

          {/* 내 좋아요 브랜드 숏컷 */}
          <MyBrandLikesShortcut />
        </div>

        {/* 구분선 */}
        <div className="border-t border-gray-200 my-6"></div>

        {/* 미션 리스트 섹션 */}
        <div>
          <h2 className="text-xl font-bold text-black mb-4">
            참여 가능한 식사미션
          </h2>
          {filteredCampaigns.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">현재 진행 중인 캠페인이 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.map((campaign: any) => {
                const nearestRun = campaign.runs?.[0];
                if (!nearestRun) return null;

                return (
                  <div
                    key={campaign.id}
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    className="w-full rounded-[12px] overflow-hidden shadow-md bg-white cursor-pointer hover:shadow-lg transition-shadow"
                  >
                    <div className="relative w-full aspect-[4/3]">
                      {campaign.productImageUrl ? (
                        <img
                          src={campaign.productImageUrl}
                          alt={campaign.productName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400">이미지 없음</span>
                        </div>
                      )}
                      {/* 품절 오버레이 */}
                      {nearestRun?.isFull && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
                          <span className="text-white text-xl font-semibold">
                            품절
                          </span>
                        </div>
                      )}
                      {/* 그라데이션 오버레이 */}
                      <div className="absolute bottom-0 left-0 w-full h-3/5 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />

                      {/* 상단 배지들 */}
                      <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start">
                        {/* 시작 시간 배지 */}
                        <div className="inline-flex w-fit px-2.5 py-1 rounded-lg bg-white/95 backdrop-blur-sm shadow-sm">
                          <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                            {getFormattedRunTime(nearestRun)}
                          </span>
                        </div>
                        {/* 할인율 배지 */}
                        {campaign.originalPrice > 0 &&
                          (() => {
                            const discountRate = Math.round(
                              ((campaign.originalPrice -
                                campaign.discountPrice) /
                                campaign.originalPrice) *
                                100
                            );
                            return (
                              discountRate > 0 && (
                                <div className="inline-flex w-fit px-2.5 py-1 rounded-lg bg-red-500/95 backdrop-blur-sm shadow-sm">
                                  <span className="text-xs font-bold text-white whitespace-nowrap">
                                    {discountRate}% 할인
                                  </span>
                                </div>
                              )
                            );
                          })()}
                        {/* 참여 제한 배지 */}
                        {campaign.contract &&
                          getParticipationLimitText(campaign.contract) && (
                            <div className="inline-flex flex-col w-fit px-2.5 py-1 rounded-lg bg-purple-500/95 backdrop-blur-sm shadow-sm gap-0.5">
                              <span className="text-xs font-bold text-white whitespace-nowrap">
                                {getParticipationLimitText(campaign.contract)}
                              </span>
                              {getRemainingParticipationCountText(
                                campaign.contract
                              ) && (
                                <span className="text-xs text-purple-100 whitespace-nowrap">
                                  {getRemainingParticipationCountText(
                                    campaign.contract
                                  )}
                                </span>
                              )}
                            </div>
                          )}
                      </div>

                      {/* 하단 정보 오버레이 */}
                      <div className="absolute bottom-0 left-0 right-0 p-3.5 text-white">
                        <h3 className="font-bold text-base mb-1.5 line-clamp-1 drop-shadow-md">
                          {campaign.productName}
                        </h3>
                        <div className="flex items-baseline gap-2 mb-2">
                          <span className="text-xs text-gray-200 line-through drop-shadow-md">
                            {campaign.originalPrice.toLocaleString()}원
                          </span>
                          <span className="font-bold text-xl drop-shadow-md">
                            {campaign.discountPrice.toLocaleString()}원
                          </span>
                        </div>
                        {/* 페이백 칩 */}
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="px-2 py-1 rounded-md bg-green-500/95 backdrop-blur-sm text-xs font-bold text-white">
                            페이백 {campaign.paybackPrice.toLocaleString()}원
                          </span>
                        </div>
                        {/* 남은 개수 표기 */}
                        {nearestRun?.maxParticipants !== null &&
                          nearestRun?.remainingSlots !== null && (
                            <div className="text-xs text-gray-200 drop-shadow-md">
                              {nearestRun.isFull
                                ? "품절"
                                : `${nearestRun.remainingSlots}개 남았어요!`}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 참여방법 바텀시트 */}
      <ParticipationGuideSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setIsBottomSheetOpen(false)}
      />
    </div>
  );
};

export default CampaignsListPage;
